/**
 * Chat Screen — main chat UI with message list, input, and rich blocks.
 *
 * Bot responses are instant (<200ms). LLM responses show typing indicator.
 * Polls every 3s only when escalated (human agent scenario).
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useLocalSearchParams, Stack } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    FlatList,
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { ChatBubble } from "@/components/chat/ChatBubble";
import { ChatInput } from "@/components/chat/ChatInput";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { fetchVisits } from "@/lib/api";
import {
    fetchMessages,
    sendMessage,
    markAsRead,
    resolveConversation,
    type LocalMessage,
    type DeliveryStatus
} from "@/lib/chat-api";
import { colors, fonts, radius, spacing, typography } from "@/lib/theme";

export default function ChatScreen() {
    const { conversationId } = useLocalSearchParams<{ conversationId: string }>();
    const [messages, setMessages] = useState<LocalMessage[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [convStatus, setConvStatus] = useState<string>("active");
    const [rnId, setRnId] = useState<string | null>(null);
    const flatListRef = useRef<FlatList>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Discover RN ID from visits
    useEffect(() => {
        fetchVisits()
            .then((visits) => {
                if (visits.length > 0) {
                    setRnId(String(visits[0].rnId));
                }
            })
            .catch(() => {});
    }, []);

    // Load messages
    const loadMessages = useCallback(async () => {
        if (!conversationId) {
            return;
        }
        try {
            const data = await fetchMessages(conversationId);
            setConvStatus(data.conversation.status);

            const local: LocalMessage[] = data.messages.map((m) => ({
                ...m,
                deliveryStatus: (m.role === "rn"
                    ? m.deliveredAt
                        ? "delivered"
                        : "sent"
                    : "delivered") as DeliveryStatus,
                localId: String(m.id)
            }));
            setMessages(local);

            // Mark as read
            markAsRead(conversationId).catch(() => {});
        } catch (err) {
            console.error("Failed to load messages:", err);
        }
    }, [conversationId]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    // Poll for new messages when escalated (human agent may reply)
    useEffect(() => {
        if (convStatus === "escalated") {
            pollRef.current = setInterval(loadMessages, 3000);
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
            }
        };
    }, [convStatus, loadMessages]);

    const sendingRef = useRef(false);

    const handleSend = async (text: string) => {
        if (!conversationId || sendingRef.current || !rnId) {
            return;
        }
        sendingRef.current = true;
        setIsSending(true);

        const localId = `local-${Date.now()}`;
        const optimistic: LocalMessage = {
            id: localId,
            conversationId,
            role: "rn",
            senderId: rnId,
            senderName: null,
            senderType: "rn",
            content: text,
            responseBlocks: null,
            llmModel: null,
            llmLatencyMs: null,
            createdAt: new Date().toISOString(),
            deliveredAt: null,
            readAt: null,
            deliveryStatus: "sending",
            localId
        };

        setMessages((prev) => [...prev, optimistic]);
        setIsTyping(true);

        try {
            const result = await sendMessage(conversationId, text, rnId);

            // Update optimistic message
            setMessages((prev) =>
                prev.map((m) =>
                    m.localId === localId
                        ? {
                              ...result.userMessage,
                              deliveryStatus: "delivered" as DeliveryStatus,
                              localId
                          }
                        : m
                )
            );

            // Add response
            if (result.response) {
                const responseLocal: LocalMessage = {
                    ...result.response,
                    deliveryStatus: "delivered",
                    localId: String(result.response.id)
                };
                setMessages((prev) => [...prev, responseLocal]);
            }

            // Update conversation status if escalation/resolve happened
            if (
                result.response?.content?.includes("escalated") ||
                result.response?.content?.includes("connected you with")
            ) {
                setConvStatus("escalated");
            }
        } catch (_err) {
            // Mark as failed
            setMessages((prev) =>
                prev.map((m) =>
                    m.localId === localId ? { ...m, deliveryStatus: "failed" as DeliveryStatus } : m
                )
            );
        } finally {
            sendingRef.current = false;
            setIsSending(false);
            setIsTyping(false);
        }
    };

    const handleChipTap = (value: string) => {
        handleSend(value);
    };

    const handleRetry = (localId: string) => {
        const msg = messages.find((m) => m.localId === localId);
        if (msg?.content) {
            // Remove failed message then resend after state settles
            setMessages((prev) => prev.filter((m) => m.localId !== localId));
            setTimeout(() => handleSend(msg.content ?? ""), 50);
        }
    };

    const handleEndChat = async () => {
        if (!conversationId) {
            return;
        }
        try {
            await resolveConversation(conversationId);
            setConvStatus("resolved");
        } catch (err) {
            console.error("Failed to resolve:", err);
        }
    };

    const renderItem = ({ item }: { item: LocalMessage }) => (
        <ChatBubble
            message={item}
            deliveryStatus={item.deliveryStatus}
            onChipTap={handleChipTap}
            onRetry={() => handleRetry(item.localId)}
        />
    );

    const statusDotColor = convStatus === "escalated" ? colors.pink400 : colors.teal400;

    return (
        <>
            <Stack.Screen
                options={{
                    headerShown: true,
                    title: "",
                    headerStyle: {
                        backgroundColor: colors.surface
                    },
                    headerTintColor: colors.textPrimary,
                    headerLeft: () => (
                        <View style={styles.headerLeft}>
                            <Text style={styles.headerTitle}>Chat</Text>
                            <View
                                style={[
                                    styles.headerStatusDot,
                                    { backgroundColor: statusDotColor }
                                ]}
                            />
                        </View>
                    ),
                    headerRight: () =>
                        convStatus !== "resolved" ? (
                            <TouchableOpacity onPress={handleEndChat} style={styles.endChatBtn}>
                                <Text style={styles.endChatText}>End Chat</Text>
                            </TouchableOpacity>
                        ) : null
                }}
            />
            <KeyboardAvoidingView
                style={styles.container}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
                keyboardVerticalOffset={Platform.OS === "ios" ? 90 : 0}
            >
                {/* Escalation banner */}
                {convStatus === "escalated" && (
                    <View style={styles.banner}>
                        <MaterialCommunityIcons
                            name="account-voice"
                            size={16}
                            color={colors.warning500}
                        />
                        <Text style={styles.bannerText}>
                            Connected to a human agent — they'll respond shortly
                        </Text>
                    </View>
                )}

                {/* Messages */}
                <FlatList
                    ref={flatListRef}
                    data={messages}
                    renderItem={renderItem}
                    keyExtractor={(item) => item.localId}
                    contentContainerStyle={styles.listContent}
                    onContentSizeChange={() => flatListRef.current?.scrollToEnd({ animated: true })}
                    showsVerticalScrollIndicator={false}
                    keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "none"}
                    keyboardShouldPersistTaps="handled"
                    automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                />

                {/* Typing indicator */}
                {isTyping && <TypingIndicator />}

                {/* Resolved footer */}
                {convStatus === "resolved" ? (
                    <View style={styles.resolvedFooter}>
                        <MaterialCommunityIcons
                            name="check-circle"
                            size={16}
                            color={colors.success}
                        />
                        <Text style={styles.resolvedText}>This conversation has been resolved</Text>
                    </View>
                ) : (
                    <ChatInput
                        onSend={handleSend}
                        disabled={isSending}
                        placeholder={
                            convStatus === "escalated"
                                ? "Message your agent..."
                                : "Type a message or tap a chip..."
                        }
                    />
                )}
            </KeyboardAvoidingView>
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    listContent: {
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.xs
    },

    // ── Header ──
    headerLeft: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    headerTitle: {
        fontSize: 17,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    headerStatusDot: {
        width: 8,
        height: 8,
        borderRadius: 4
    },

    // ── Escalation Banner ──
    banner: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.warning50,
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.sm,
        borderBottomWidth: 1.5,
        borderBottomColor: colors.border
    },
    bannerText: {
        ...typography.bodySmall,
        color: colors.textSecondary,
        flex: 1
    },

    // ── Resolved Footer ──
    resolvedFooter: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        paddingVertical: spacing.base,
        backgroundColor: colors.surface,
        borderTopWidth: 1.5,
        borderTopColor: colors.border
    },
    resolvedText: {
        ...typography.body,
        color: colors.textSecondary
    },

    // ── End Chat ──
    endChatBtn: {
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        borderRadius: radius.sm,
        backgroundColor: colors.error50
    },
    endChatText: {
        ...typography.bodySmall,
        fontFamily: fonts.semiBold,
        color: colors.error500
    }
});
